import { createSlice, type Draft, type PayloadAction } from '@reduxjs/toolkit';

import type {
  ArquivoDoPacote,
  AutosDoProcesso,
  ChaveDaSecao,
  EtapaGeracao,
  FormatoDownload,
  OpcoesDosAutos,
  PacoteDosAutos,
  PacoteEmEspera,
  PacoteRecebido,
  RecusaDosAutos,
  RegistroDeAnexos,
  SecaoDosAutos,
} from '../types/baixarProcesso.types';
import {
  avisoDeSecoesQueSairam,
  chavesDisponiveis,
  naOrdemDoDocumento,
} from '../utils/secoesDownload';
import {
  abrirPacoteDoAviso,
  acompanharPacote,
  assinarArquivo,
  avisarPacotePorEmail,
  carregarRegistroDeAnexos,
  listarAutos,
  pacoteConsultado,
  pedirPacote,
  recusaDefinitiva,
  registrarCienciaDosAnexos,
} from './actions';

interface BaixarProcessoState {
  menuAberto: boolean;
  painelAberto: boolean;
  /** `null` enquanto a Secretaria ainda escolhe o formato dentro do painel. */
  formato: FormatoDownload | null;
  /** O cartão marcado na escolha de formato, antes do "Continuar". */
  formatoEmEscolha: FormatoDownload | null;
  etapa: EtapaGeracao;
  /** Seções e pacotes do processo, como o servidor os devolveu agora. */
  opcoes: OpcoesDosAutos | null;
  carregandoOpcoes: boolean;
  requestIdOpcoes: string | null;
  secoesMarcadas: ChaveDaSecao[];
  /** De que processo é a marcação: em outro processo ela recomeça pelas seções disponíveis. */
  processoDaMarcacao: string | null;
  /** Os dados mudaram entre a seleção e a confirmação; o painel pede para revisar. */
  avisoDaSelecao: string | null;
  pacote: PacoteDosAutos | null;
  /** O servidor devolveu um pacote que já existia: nada foi montado de novo. */
  reaproveitado: boolean;
  erroGeracao: string | null;
  /** O `erroCodigo` do pacote que falhou: decide se "Tentar novamente" faz sentido. */
  codigoDaFalha: string | null;
  /**
   * Quem pode escrever o resultado. `null` depois de cancelar ou fechar: o pedido que já estava
   * em voo responde no vazio em vez de jogar o painel para "pronto" sem o usuário pedir.
   */
  requestIdGeracao: string | null;
  processoIdGeracao: string | null;
  pacoteEmEspera: PacoteEmEspera | null;
  /** Arquivo cuja URL assinada está sendo pedida — o botão dele fica ocupado. */
  arquivoEmPreparo: ArquivoDoPacote | null;
  /** O registro de anexos do pacote pronto na tela. */
  registroDeAnexos: RegistroDeAnexos | null;
  carregandoRegistro: boolean;
  /**
   * O pedido do registro que vale: a resposta de outro (abortado, ou de um pacote que saiu da
   * tela) não mexe no estado. Pelo id, e não pelo `meta.aborted`: o abort do cleanup chega DEPOIS
   * do `pending` do pedido seguinte, e desligaria o carregamento dele.
   */
  requestIdRegistro: string | null;
  /** A busca do registro falhou: sem a lista, a ciência não pode ser dada. */
  falhaNoRegistro: boolean;
  enviandoCiencia: boolean;
}

const initialState: BaixarProcessoState = {
  menuAberto: false,
  painelAberto: false,
  formato: 'completo',
  formatoEmEscolha: null,
  etapa: 'selecao',
  opcoes: null,
  carregandoOpcoes: false,
  requestIdOpcoes: null,
  secoesMarcadas: [],
  processoDaMarcacao: null,
  avisoDaSelecao: null,
  pacote: null,
  reaproveitado: false,
  erroGeracao: null,
  codigoDaFalha: null,
  requestIdGeracao: null,
  processoIdGeracao: null,
  pacoteEmEspera: null,
  arquivoEmPreparo: null,
  registroDeAnexos: null,
  carregandoRegistro: false,
  requestIdRegistro: null,
  falhaNoRegistro: false,
  enviandoCiencia: false,
};

function limparResultado(state: Draft<BaixarProcessoState>) {
  state.pacote = null;
  state.reaproveitado = false;
  state.erroGeracao = null;
  state.codigoDaFalha = null;
  state.requestIdGeracao = null;
  state.processoIdGeracao = null;
  state.pacoteEmEspera = null;
  state.arquivoEmPreparo = null;
  state.registroDeAnexos = null;
  state.carregandoRegistro = false;
  state.requestIdRegistro = null;
  state.falhaNoRegistro = false;
  state.enviandoCiencia = false;
}

/** A resposta é do registro do pacote que está na tela (não de um anterior, já trocado). */
function doPacoteNaTela(state: Draft<BaixarProcessoState>, pacoteId: string): boolean {
  return state.pacote?.pacoteId === pacoteId;
}

function disponiveisAgora(state: Draft<BaixarProcessoState>): ChaveDaSecao[] {
  return state.opcoes ? chavesDisponiveis(state.opcoes.secoes) : [];
}

/**
 * Só o último pedido, e só do processo em que ele foi feito, escreve no painel. Sem o segundo
 * teste, pedir no processo A e navegar para o B oferecia o pacote de A na tela de B.
 */
function respostaAindaVale(
  state: Draft<BaixarProcessoState>,
  meta: { requestId: string; arg: { processoId: string } },
): boolean {
  return (
    state.requestIdGeracao === meta.requestId && state.processoIdGeracao === meta.arg.processoId
  );
}

/**
 * A resposta é do pacote que o painel ainda acompanha. Cancelar, fechar ou trocar de formato zera
 * `pacoteEmEspera`: o acompanhamento que terminar depois disso não reabre o painel em "pronto".
 */
function aindaAcompanhando(state: Draft<BaixarProcessoState>, pacoteId: string): boolean {
  return state.pacoteEmEspera?.pacoteId === pacoteId;
}

function aoPedir(
  state: Draft<BaixarProcessoState>,
  meta: { requestId: string; arg: { processoId: string } },
) {
  limparResultado(state);
  state.requestIdGeracao = meta.requestId;
  state.processoIdGeracao = meta.arg.processoId;
  state.etapa = 'gerando';
  state.avisoDaSelecao = null;
}

/**
 * Pronto na primeira resposta sai na hora. Ainda na fila ou montando, o painel passa a acompanhar
 * — e a espera conta do instante da resposta, não de quando o pacote nasceu: um pacote antigo
 * reaproveitado ainda na fila não pode abrir a pergunta "continuar esperando?" na hora.
 */
function aoReceber(
  state: Draft<BaixarProcessoState>,
  recebido: PacoteRecebido,
  autos: AutosDoProcesso,
  reaproveitavel: boolean,
) {
  state.pacote = recebido.pacote as Draft<PacoteDosAutos>;
  if (recebido.pacote.status === 'PRONTO') {
    state.etapa = 'pronto';
    state.reaproveitado = reaproveitavel;
    return;
  }
  state.pacoteEmEspera = {
    porta: autos.porta,
    processoId: autos.processoId,
    pacoteId: recebido.pacote.pacoteId,
    desde: recebido.respondidoEm,
    decisaoAte: null,
    esperaConfirmada: false,
    avisoPorEmailPedido: false,
  };
}

function aoRecusar(
  state: Draft<BaixarProcessoState>,
  recusa: RecusaDosAutos | undefined,
  mensagemPadrao = 'Não foi possível gerar o PDF do processo.',
) {
  if (recusaDefinitiva(recusa)) {
    state.etapa = recusa.motivo;
    return;
  }
  // O pedido não saiu: volta para a seleção, que o hook relê do servidor.
  if (recusa?.motivo === 'secoesIndisponiveis') {
    state.etapa = 'selecao';
    state.avisoDaSelecao = recusa.mensagem;
    return;
  }
  state.etapa = 'falha';
  state.erroGeracao = recusa?.mensagem ?? mensagemPadrao;
  state.codigoDaFalha = recusa?.erroCodigo ?? null;
}

/**
 * Relida a seleção depois de uma seção sumir (409 `SECOES_INDISPONIVEIS`), o aviso diz QUAL saiu:
 * a marcação já perdeu a seção sozinha, e só o aviso genérico deixava a pessoa procurando.
 */
function avisoDasSecoesRetiradas(
  state: Draft<BaixarProcessoState>,
  secoes: readonly SecaoDosAutos[],
): void {
  if (state.avisoDaSelecao === null) return;
  const retiradas = secoes.filter(
    (secao) => !secao.disponivel && state.secoesMarcadas.includes(secao.chave),
  );
  if (retiradas.length === 0) return;
  state.avisoDaSelecao = avisoDeSecoesQueSairam(retiradas.map((secao) => secao.titulo));
}

const baixarProcessoSlice = createSlice({
  name: 'baixarProcesso',
  initialState,
  reducers: {
    menuFechado: (state) => {
      state.menuAberto = false;
    },
    /**
     * "Trocar formato" no escritório FECHA o painel e devolve ao menu do cabeçalho.
     *
     * O desenho mostra o menu por cima do painel, mas o painel é um diálogo modal (Radix): ele
     * prende o foco e desliga o ponteiro fora do seu conteúdo, então um menu ancorado no botão do
     * cabeçalho abriria visível e morto. Trocar modal por não-modal só para isso custaria o focus
     * trap do painel — caro para uma sobreposição.
     */
    trocaDeFormatoPedida: (state) => {
      state.painelAberto = false;
      state.menuAberto = true;
      state.etapa = 'selecao';
      limparResultado(state);
    },
    /** A Secretaria não tem o menu do cabeçalho: escolhe o formato dentro do próprio painel. */
    painelAbertoParaEscolherFormato: (state) => {
      state.painelAberto = true;
      state.menuAberto = false;
      state.formato = null;
      state.formatoEmEscolha = null;
      state.etapa = 'formato';
      limparResultado(state);
    },
    /** "Trocar formato" dentro do painel: volta à escolha com o formato atual já marcado. */
    escolhaDeFormatoReaberta: (state) => {
      state.formatoEmEscolha = state.formato;
      state.formato = null;
      state.etapa = 'formato';
      limparResultado(state);
    },
    formatoMarcado: (state, action: PayloadAction<FormatoDownload>) => {
      state.formatoEmEscolha = action.payload;
    },
    formatoEscolhido: (state, action: PayloadAction<FormatoDownload>) => {
      state.formato = action.payload;
      state.formatoEmEscolha = null;
      state.menuAberto = false;
      state.painelAberto = true;
      state.etapa = 'selecao';
      state.avisoDaSelecao = null;
      limparResultado(state);
    },
    painelFechado: (state) => {
      state.painelAberto = false;
      state.menuAberto = false;
      state.formatoEmEscolha = null;
      state.etapa = 'selecao';
      state.avisoDaSelecao = null;
      limparResultado(state);
    },
    secaoAlternada: (state, action: PayloadAction<ChaveDaSecao>) => {
      const chave = action.payload;
      if (!disponiveisAgora(state).includes(chave)) return;
      state.secoesMarcadas = state.secoesMarcadas.includes(chave)
        ? state.secoesMarcadas.filter((item) => item !== chave)
        : naOrdemDoDocumento([...state.secoesMarcadas, chave]);
    },
    todasAsSecoesAlternadas: (state) => {
      const disponiveis = disponiveisAgora(state);
      const todasMarcadas = disponiveis.every((chave) => state.secoesMarcadas.includes(chave));
      state.secoesMarcadas = todasMarcadas ? [] : disponiveis;
    },
    voltouParaSelecao: (state) => {
      state.etapa = 'selecao';
      limparResultado(state);
    },
    /** Passou o tempo de espera: o painel pergunta se a pessoa quer continuar. `ate` é ISO. */
    decisaoPedida: (state, action: PayloadAction<{ ate: string }>) => {
      if (state.etapa !== 'gerando' || !state.pacoteEmEspera) return;
      if (state.pacoteEmEspera.esperaConfirmada || state.pacoteEmEspera.avisoPorEmailPedido) return;
      state.etapa = 'aguardandoDecisao';
      state.pacoteEmEspera.decisaoAte = action.payload.ate;
    },
    /**
     * "Continuar esperando": uma vez só — daqui o painel espera até terminar, sem perguntar.
     * Depois que o e-mail foi pedido, o clique não vale: a resposta do pedido levaria o painel
     * para "segundo plano" logo em seguida, ao contrário do que a pessoa escolheu.
     */
    esperaConfirmada: (state) => {
      if (state.etapa !== 'aguardandoDecisao' || !state.pacoteEmEspera) return;
      if (state.pacoteEmEspera.avisoPorEmailPedido) return;
      state.etapa = 'gerando';
      state.pacoteEmEspera.decisaoAte = null;
      state.pacoteEmEspera.esperaConfirmada = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listarAutos.pending, (state, action) => {
        state.requestIdOpcoes = action.meta.requestId;
        state.carregandoOpcoes = true;
        state.opcoes = null;
        // "Tentar novamente" depois de a LISTA falhar: o painel volta à seleção que está relendo,
        // senão ficaria em `falha` com as opções já carregadas.
        if (state.etapa === 'falha' && state.formato === 'secoes') {
          state.etapa = 'selecao';
          state.erroGeracao = null;
        }
      })
      // Marcação inicial = tudo o que está disponível. Relendo o MESMO processo (a revisão depois
      // de uma seção sumir, ou o painel reaberto), vale a marcação anterior menos o que deixou
      // de estar disponível.
      .addCase(listarAutos.fulfilled, (state, action) => {
        if (state.requestIdOpcoes !== action.meta.requestId) return;
        state.carregandoOpcoes = false;
        state.opcoes = action.payload as Draft<OpcoesDosAutos>;
        const disponiveis = chavesDisponiveis(action.payload.secoes);
        const mesmoProcesso = state.processoDaMarcacao === action.meta.arg.processoId;
        if (mesmoProcesso) avisoDasSecoesRetiradas(state, action.payload.secoes);
        state.secoesMarcadas = mesmoProcesso
          ? state.secoesMarcadas.filter((chave) => disponiveis.includes(chave))
          : disponiveis;
        state.processoDaMarcacao = action.meta.arg.processoId;
      })
      .addCase(listarAutos.rejected, (state, action) => {
        if (state.requestIdOpcoes !== action.meta.requestId) return;
        state.carregandoOpcoes = false;
        if (!state.painelAberto || state.etapa !== 'selecao') return;
        aoRecusar(state, action.payload);
      })
      .addCase(pedirPacote.pending, (state, action) => aoPedir(state, action.meta))
      .addCase(pedirPacote.fulfilled, (state, action) => {
        if (!respostaAindaVale(state, action.meta)) return;
        aoReceber(state, action.payload, action.meta.arg, true);
      })
      .addCase(pedirPacote.rejected, (state, action) => {
        if (!respostaAindaVale(state, action.meta)) return;
        aoRecusar(state, action.payload);
      })
      .addCase(abrirPacoteDoAviso.pending, (state, action) => aoPedir(state, action.meta))
      // O pacote do e-mail diz o próprio formato: com seções, é o documento recortado.
      .addCase(abrirPacoteDoAviso.fulfilled, (state, action) => {
        if (!respostaAindaVale(state, action.meta)) return;
        if (action.payload.pacote.secoes) state.formato = 'secoes';
        aoReceber(state, action.payload, action.meta.arg, false);
      })
      .addCase(abrirPacoteDoAviso.rejected, (state, action) => {
        if (!respostaAindaVale(state, action.meta)) return;
        aoRecusar(state, action.payload);
      })
      .addCase(pacoteConsultado, (state, action) => {
        if (!aindaAcompanhando(state, action.payload.pacoteId)) return;
        state.pacote = action.payload as Draft<PacoteDosAutos>;
      })
      .addCase(acompanharPacote.fulfilled, (state, action) => {
        if (!aindaAcompanhando(state, action.meta.arg.pacoteId)) return;
        state.etapa = 'pronto';
        state.pacote = action.payload as Draft<PacoteDosAutos>;
        state.reaproveitado = false;
        state.pacoteEmEspera = null;
      })
      .addCase(acompanharPacote.rejected, (state, action) => {
        if (action.meta.aborted || !aindaAcompanhando(state, action.meta.arg.pacoteId)) return;
        // Servidor sem responder não é pacote com falha: o painel segue esperando e o hook manda
        // para o e-mail, que chega quando o servidor terminar.
        if (action.payload?.motivo === 'semResposta') return;
        state.pacoteEmEspera = null;
        aoRecusar(state, action.payload?.recusa);
      })
      .addCase(avisarPacotePorEmail.pending, (state, action) => {
        if (action.meta.arg.aoSair || !aindaAcompanhando(state, action.meta.arg.pacoteId)) return;
        if (!state.pacoteEmEspera) return;
        state.pacoteEmEspera.avisoPorEmailPedido = true;
        state.pacoteEmEspera.decisaoAte = null;
      })
      .addCase(avisarPacotePorEmail.fulfilled, (state, action) => {
        if (action.meta.arg.aoSair || !aindaAcompanhando(state, action.meta.arg.pacoteId)) return;
        state.etapa = 'segundoPlano';
        state.pacoteEmEspera = null;
      })
      .addCase(avisarPacotePorEmail.rejected, (state, action) => {
        if (action.meta.arg.aoSair || !aindaAcompanhando(state, action.meta.arg.pacoteId)) return;
        state.pacoteEmEspera = null;
        aoRecusar(state, action.payload, 'Não foi possível agendar o aviso por e-mail.');
      })
      .addCase(assinarArquivo.pending, (state, action) => {
        state.arquivoEmPreparo = action.meta.arg.arquivo;
      })
      .addCase(assinarArquivo.fulfilled, (state) => {
        state.arquivoEmPreparo = null;
      })
      // Perdeu o acesso com o pacote já pronto: os botões de download não valem mais. Falha
      // passageira de assinatura fica no aviso do hook, com o pacote na tela.
      .addCase(assinarArquivo.rejected, (state, action) => {
        state.arquivoEmPreparo = null;
        // O servidor ainda deve a ciência (outra aba, ou a tela antiga): o painel volta a pedi-la.
        if (action.payload?.motivo === 'cienciaPendente' && state.pacote) {
          state.pacote.cienciaPendente = true;
          return;
        }
        if (!recusaDefinitiva(action.payload)) return;
        limparResultado(state);
        state.etapa = action.payload.motivo;
      })
      .addCase(carregarRegistroDeAnexos.pending, (state, action) => {
        if (!doPacoteNaTela(state, action.meta.arg.pacoteId)) return;
        state.carregandoRegistro = true;
        state.falhaNoRegistro = false;
        state.requestIdRegistro = action.meta.requestId;
      })
      .addCase(carregarRegistroDeAnexos.fulfilled, (state, action) => {
        if (state.requestIdRegistro !== action.meta.requestId) return;
        state.carregandoRegistro = false;
        state.requestIdRegistro = null;
        state.registroDeAnexos = action.payload as Draft<RegistroDeAnexos>;
      })
      .addCase(carregarRegistroDeAnexos.rejected, (state, action) => {
        if (state.requestIdRegistro !== action.meta.requestId) return;
        state.carregandoRegistro = false;
        state.requestIdRegistro = null;
        // Abortado pelo próprio painel (o pacote saiu da tela) não é falha a mostrar.
        if (action.meta.aborted) return;
        if (!recusaDefinitiva(action.payload)) {
          state.falhaNoRegistro = true;
          return;
        }
        limparResultado(state);
        state.etapa = action.payload.motivo;
      })
      .addCase(registrarCienciaDosAnexos.pending, (state) => {
        state.enviandoCiencia = true;
      })
      .addCase(registrarCienciaDosAnexos.fulfilled, (state, action) => {
        state.enviandoCiencia = false;
        if (!doPacoteNaTela(state, action.meta.arg.pacoteId) || !state.pacote) return;
        state.pacote.cienciaPendente = false;
        if (state.registroDeAnexos) {
          state.registroDeAnexos.cienciaPendente = false;
          state.registroDeAnexos.cienciaEm = action.payload.cienciaEm;
        }
      })
      .addCase(registrarCienciaDosAnexos.rejected, (state, action) => {
        state.enviandoCiencia = false;
        if (!doPacoteNaTela(state, action.meta.arg.pacoteId) || !recusaDefinitiva(action.payload)) {
          return;
        }
        limparResultado(state);
        state.etapa = action.payload.motivo;
      });
  },
});

export const {
  decisaoPedida,
  escolhaDeFormatoReaberta,
  esperaConfirmada,
  formatoEscolhido,
  formatoMarcado,
  menuFechado,
  painelAbertoParaEscolherFormato,
  painelFechado,
  secaoAlternada,
  todasAsSecoesAlternadas,
  trocaDeFormatoPedida,
  voltouParaSelecao,
} = baixarProcessoSlice.actions;

export const baixarProcessoReducer = baixarProcessoSlice.reducer;
